import { Component, OnInit, Input } from '@angular/core';
import { NgbModal, ModalDismissReasons } from '@ng-bootstrap/ng-bootstrap';

import { MatterUser } from '../../../models/matter-user';

import { AlertService } from '../../../services/common/alert.service';
import { MatterUserService } from '../../../services/matters/matter-user.service';

@Component({
  selector: 'app-matter-user-index',
  templateUrl: './matter-user-index.component.html',
  styleUrls: ['./matter-user-index.component.css'],
})
export class MatterUserIndexComponent implements OnInit {
  /**
   * Matter id of the matter page
   */
  @Input() matter_id: number;

  /**
   * Indicates if current user can view matter user income page
   * @type {boolean}
   */
  allowedToViewUserIncome: boolean = false;

  /**
   * Search params
   * @type {any}
   */
  search_params: any = {
    showInactive: false,
  };

  /**
   * List of matter users for matter
   * @type {Array<MatterUser>}
   */
  matter_users: Array<MatterUser> = [];

  /**
   * Indicates if component is loading matter users
   * @type {boolean}
   */
  loading: boolean = true;

  /**
   * Indicates if new matter user form is to be shown
   * @type {boolean}
   */
  showNewUserForm: boolean = false;

  /**
   * Holds matter user to be deleted for dialog
   * @type {MatterUser}
   */
  matterUserToBeDeleted: MatterUser | null;

  /**
   * Constructor
   * @param {AlertService}      private alertService      Service to show bootstrap based alerts
   * @param {MatterUserService} private matterUserService Service to access matter users controller
   * @param {NgbModal}          private modalService      Service to show modal dialogs
   */
  constructor(private alertService: AlertService, private matterUserService: MatterUserService, private modalService: NgbModal) {}

  // Component lifecycle methods

  /**
   * Called on component initialization
   */
  ngOnInit() {
    if (this.matter_id)
      this.matterUserService.allowedToViewUserIncome(this.matter_id).then((response_json) => {
        this.allowedToViewUserIncome = response_json.allowed_to_view;
        this.loadMatterUsers();
      });
  }

  // Component methods

  /**
   * Load matter users
   */
  loadMatterUsers() {
    this.search_params.matter_id = this.matter_id;
    this.matterUserService.getMatterUsers(this.search_params).then((response_json) => {
      this.loading = false;
      this.matter_users = response_json;
    });
  }

  /**
   * Edit matter user
   * @param {MatterUser} matter_user Matter user to edit
   */
  editMatterUser(matter_user: MatterUser) {
    matter_user.editing = true;
  }

  /**
   * Result of matter user form for new matter user or editing a matter user
   * @param {string}        event Result
   * @param {MatterUser =     null}        matter_user Matter user edited (sent only if the form is an edit form)
   */
  formResult(event: string, matter_user: MatterUser | null = null) {
    if (event == 'Form Cancelled') {
      if (matter_user) matter_user.editing = false;
      else this.showNewUserForm = false;
    } else if (event == 'Form Saved') {
      if (matter_user) matter_user.editing = false;
      else this.showNewUserForm = false;
      this.loadMatterUsers();
    }
  }

  /**
   * Confirm deletion for matter user
   * @param {MatterUser} matter_user Matter user to delete
   * @param {NgTemplate}     modal       Modal template for confirm dialog
   */
  confirmMatterUserDelete(matter_user: MatterUser, modal: any) {
    this.matterUserToBeDeleted = matter_user;
    this.modalService.open(modal).result.then(
      (result) => {
        this.deleteMatterUser();
      },
      (reason) => {}
    );
  }

  /**
   * Delete matter user, already selected by calling confirmMatterUserDelete method
   */
  deleteMatterUser() {
    this.matterUserToBeDeleted!.deleting = true;
    let scope = this;
    this.matterUserService
      .deleteMatterUser(this.matterUserToBeDeleted!)
      .then((response_json) => {
        this.matterUserToBeDeleted!.deleting = false;
        this.matterUserToBeDeleted = null;
        if (response_json.text == 'Matter user deleted') {
          this.loadMatterUsers();
          this.alertService.alert_status_success(response_json.text);
        } else this.alertService.alert_status_danger(response_json.text || 'Error deleting matter user');
      })
      .catch(() => {
        scope.alertService.alert_status_danger('Error deleting matter user');
        this.matterUserToBeDeleted!.deleting = false;
        this.matterUserToBeDeleted = null;
      });
  }

  /**
   * Get dismiss reason for modal dialog close
   * @param  {any}    reason Reason provided on dialog close
   * @return {string}        Text response for dialog dismiss reason
   */
  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }
}
